import { useEffect, useRef, useState } from 'react';
import { strings } from '@/content/strings';
import { AGES, settingsStore, useSettings } from '@/game/settings';
import { cx } from '@/lib/cx';
import { QaPanel } from '@/qa/QaPanel';
import { Frame } from '@/ui/Frame';
import { Surface } from '@/ui/Surface';
import styles from './SettingsDialog.module.css';
import { resetGame } from '@/game/reset';
import { navigate } from '@/app/router';

/** Grown-ups' settings: the player's age, and starting over. */
export function SettingsDialog({ onClose }: { onClose: () => void }) {
  const t = strings.settings;
  const { age } = useSettings();
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const first = useRef<HTMLDivElement>(null);
  useEffect(() => {
    first.current?.querySelector('button')?.focus();
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && age !== null) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [age, onClose]);

  return (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-title"
    >
      <div className={styles.panel}>
        <Frame>
          <Surface variant="screen" className={styles.screen}>
            <h2 id="settings-title" className={styles.title}>
              {t.title}
            </h2>
            <p id="settings-age" className={styles.question}>
              {t.ageQuestion}
            </p>
            <div
              ref={first}
              className={styles.ages}
              role="radiogroup"
              aria-labelledby="settings-age"
            >
              {AGES.map((a) => (
                <button
                  key={a}
                  type="button"
                  role="radio"
                  aria-checked={age === a}
                  className={cx(styles.age, age === a && styles.selected)}
                  onClick={() => {
                    settingsStore.setAge(a);
                  }}
                >
                  {t.ageLabel(a)}
                </button>
              ))}
            </div>
            <p className={styles.note}>{t.ageNote}</p>
            <QaPanel />

            <div className={styles.reset}>
              {resetDone ? (
                <p className={styles.note}>{t.resetDone}</p>
              ) : confirmReset ? (
                <>
                  <p className={styles.note}>{t.resetConfirm}</p>
                  <button
                    type="button"
                    className={styles.danger}
                    onClick={() => {
                      setResetDone(true);
                      resetGame();
                    }}
                  >
                    {t.resetYes}
                  </button>
                  <button
                    type="button"
                    className={styles.secondary}
                    onClick={() => {
                      setConfirmReset(false);
                    }}
                  >
                    {t.resetNo}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className={styles.secondary}
                    onClick={() => {
                      setConfirmReset(true);
                    }}
                  >
                    {t.reset}
                  </button>
                  <button
                    type="button"
                    className={styles.secondary}
                    onClick={() => {
                      onClose();
                      navigate({ page: 'debug' });
                    }}
                  >
                    {t.debug}
                  </button>
                </>
              )}
            </div>

            <button
              type="button"
              className={styles.primary}
              disabled={age === null}
              onClick={onClose}
            >
              {t.done}
            </button>
          </Surface>
        </Frame>
      </div>
    </div>
  );
}
